import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Loader2, Copy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function TwoFactorSettings({ theme = "light" }) {
  const dark = theme === "dark";
  const { user, setUser } = useAuth();
  const enabled = !!user?.totp_enabled;
  const [setup, setSetup] = useState(null); // {otpauth_uri, qr_png_base64, secret}
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);

  const card = dark ? "rounded-xl border border-zinc-800 bg-[#18181B] p-5" : "rounded-xl border border-slate-200 bg-white shadow-sm p-5";
  const heading = dark ? "text-white" : "text-slate-900";
  const muted = dark ? "text-zinc-400" : "text-slate-500";
  const inputCls = dark ? "bg-[#0A0A0A] border-zinc-700 text-white" : "bg-white border-slate-300 text-slate-900";
  const primary = dark ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-teal-600 hover:bg-teal-700 text-white";
  const well = dark ? "bg-[#0A0A0A] border border-zinc-800" : "bg-slate-50 border border-slate-200";

  const begin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data); setCode("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await api.post("/auth/2fa/confirm", { code });
      toast.success("Two-factor authentication enabled");
      setSetup(null); setCode("");
      setUser({ ...user, totp_enabled: true });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const disable = async () => {
    if (disableCode.length !== 6) return toast.error("Enter a current 6-digit code to disable");
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      toast.success("Two-factor authentication disabled");
      setDisableCode("");
      setUser({ ...user, totp_enabled: false });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className={card} data-testid="twofa-settings">
      <div className="flex items-center gap-2">
        {enabled ? <ShieldCheck className="w-5 h-5 text-emerald-500" /> : <ShieldOff className={`w-5 h-5 ${muted}`} />}
        <h2 className={`text-lg font-bold tracking-tight ${heading}`}>Two-Factor Authentication</h2>
        <span data-testid="twofa-status" className={`ml-2 text-xs px-2 py-0.5 rounded-full border ${enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : (dark ? "bg-zinc-800 text-zinc-400 border-zinc-700" : "bg-slate-100 text-slate-500 border-slate-200")}`}>
          {enabled ? "Enabled" : "Disabled"}
        </span>
      </div>
      <p className={`text-sm mt-1 ${muted}`}>Add a one-time code from Google or Microsoft Authenticator to your login.</p>

      {!enabled && !setup && (
        <Button data-testid="twofa-enable-button" onClick={begin} disabled={busy} className={`mt-4 ${primary}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enable 2FA"}
        </Button>
      )}

      {!enabled && setup && (
        <div className="mt-4 space-y-3">
          <p className={`text-sm ${muted}`}>1. Scan this QR code in your authenticator app.</p>
          <img data-testid="twofa-qr" src={`data:image/png;base64,${setup.qr_png_base64}`} alt="2FA QR code" className="w-44 h-44 rounded-lg bg-white p-2 border border-slate-200" />
          <div>
            <Label className={`text-xs ${muted}`}>Or enter this key manually</Label>
            <div className="flex items-center gap-2 mt-1">
              <code className={`flex-1 ${well} rounded px-2 py-1.5 text-xs font-mono break-all ${heading}`} data-testid="twofa-secret">{setup.secret}</code>
              <Button size="sm" variant="outline" className="h-8" onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
          <div>
            <Label className={`text-xs ${muted}`}>2. Enter the 6-digit code to confirm</Label>
            <Input data-testid="twofa-confirm-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={`mt-1 w-40 tracking-widest font-mono ${inputCls}`} />
          </div>
          <div className="flex gap-2">
            <Button data-testid="twofa-confirm-button" onClick={confirm} disabled={busy || code.length !== 6} className={primary}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm & Enable"}</Button>
            <Button variant="outline" onClick={() => { setSetup(null); setCode(""); }}>Cancel</Button>
          </div>
        </div>
      )}

      {enabled && (
        <div className="mt-4 space-y-2">
          <Label className={`text-xs ${muted}`}>Enter a current 6-digit code to turn off 2FA</Label>
          <div className="flex gap-2">
            <Input data-testid="twofa-disable-input" inputMode="numeric" maxLength={6} value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))} className={`w-40 tracking-widest font-mono ${inputCls}`} />
            <Button data-testid="twofa-disable-button" onClick={disable} disabled={busy || disableCode.length !== 6} variant="outline" className="border-rose-200 bg-white text-rose-600 hover:bg-rose-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disable 2FA"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
